'use client';
import 'dayjs/locale/es';
import 'dayjs/locale/ca';
import { Button, Form, Icon } from '@mage-ui/components';
import { cx } from '@mage-ui/styled-system/css';
import { Flex } from '@mage-ui/styled-system/jsx';
import {
	bordersWrapper,
	flexWrapper,
	gridCol,
	icon,
	inputFileBadge,
	inputFileRoot,
	inputRoot,
} from '@mage-ui/styled-system/recipes';
import { useContext, useEffect, useRef, useState } from 'react';
import { StaticTextContext } from '@/components/context/StaticTextProvider';
import { Feedback } from '@/components/ui/data-display/feedback/Feedback';
import { AlertItem } from '@/components/ui/feedback/alert-item/AlertItem';
import { EnrollmentSummary } from '@/components/ui/forms/form-online-enrollment/EnrollmentSummary';
import {
	DocumentTypeEnum,
	GenderEnum,
	OccupationEnum,
	PriceTypeEnum,
	TeacherTypeEnum,
} from '@/components/ui/forms/form-online-enrollment/enums';
import { request } from '@/services/server/request';
import { GridCol } from '../../layouts/grids/GridCol';
import { GridRow } from '../../layouts/grids/GridRow';
import { Section } from '../../layouts/sections/Section';
import { rules } from './rules';

export const FormOnlineEnrollment = ({
	locale,
	course,
	subsidies,
	group,
	language,
	pendingEnrollment,
}) => {
	const [hasStudiedBefore, setHasStudiedBefore] = useState('no');
	const [isTeacher, setIsTeacher] = useState('no');
	const [enrollment, setEnrollment] = useState(pendingEnrollment);
	const [formData, setFormData] = useState();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [backendErrors, setBackendErrors] = useState({});
	const [courseWasFull, setCourseWasFull] = useState(false);
	const alertRef = useRef(null);

	useEffect(() => {
		if (backendErrors['root.serverError']) {
			alertRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
		}
	}, [backendErrors]);

	const priceType = course?.price?.type;
	const { st } = useContext(StaticTextContext);

	const setError = (field, error) => {
		setBackendErrors((prev) => ({
			...prev,
			[field]: error,
		}));
	};

	const onSubmit = async (values) => {
		setIsSubmitting(true);
		setBackendErrors({});
		setFormData(values);

		const payload = { ...values, group_id: group?.fields?.id_backend };

		if (course.isAvailable) {
			const summaryResponse = await request(
				'/api/enrollment/summary',
				'POST',
				payload,
			);

			const courseBecameFull =
				(summaryResponse.status === 200 || summaryResponse.status === 201) &&
				!summaryResponse.data?.body?.enrollment?.course?.isAvailable;

			if (courseBecameFull) {
				setCourseWasFull(true);
				handleResponse(await request('/api/enrollment', 'POST', payload));
			} else {
				handleResponse(summaryResponse);
			}
		} else {
			handleResponse(await request('/api/enrollment', 'POST', payload));
		}

		setIsSubmitting(false);
	};

	const externalErrors = Object.fromEntries(
		Object.entries(backendErrors).map(([field, error]) => [
			field,
			{
				type: error.type ?? 'server',
				message: Array.isArray(error.message)
					? error.message.map((message) => st(message)).join(', ')
					: st(error.message),
			},
		]),
	);

	const handleResponse = ({ status, data }) => {
		switch (status) {
			case 200:
			case 201:
			case 204:
				setEnrollment(data.body.enrollment);
				break;

			case 413:
				handleServerError({ details: st('validation.files_too_large') });
				break;

			case 422:
			case 400:
			case 401:
			case 403:
			case 404:
			case 500:
				if (status === 422) {
					handleValidationError(data);
				} else {
					handleServerError(data);
				}
				break;

			default:
				break;
		}

		setIsSubmitting(false);
	};

	const handleValidationError = (data) => {
		const { body } = data;
		for (const field of Object.keys(body)) {
			setError(field, {
				type: 'server',
				message: body?.[field],
			});
		}
		setError('root.serverError', {
			message: data?.details ?? st('validation.server_error'),
		});
	};

	const handleServerError = (data) => {
		setError('root.serverError', {
			message: data?.details ?? st('validation.server_error'),
		});
	};

	const isUnder18 = (birthdate) => {
		const birthDate = new Date(birthdate);
		const today = new Date();

		let age = today.getFullYear() - birthDate.getFullYear();
		const m = today.getMonth() - birthDate.getMonth();
		if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
			age--;
		}

		return age < 18;
	};

	const EnrollmentFeedback = () =>
		course.isAvailable && !courseWasFull ? (
			<EnrollmentSummary
				locale={locale}
				group={group}
				course={course}
				language={language}
				formData={formData}
				enrollment={enrollment}
			/>
		) : (
			<Feedback
				language={locale}
				data={{
					fields: {
						title: st('forms.enrollment.feedback.unavailable.title'),
						intro: st('forms.enrollment.feedback.unavailable.subtitle'),
						content: st('forms.enrollment.feedback.unavailable.content'),
						cta_text: st('forms.enrollment.feedback.unavailable.cta_text'),
					},
				}}
			/>
		);

	return (
		<Section>
			{backendErrors['root.serverError'] && (
				<AlertItem
					ref={alertRef}
					text={backendErrors['root.serverError'].message}
				/>
			)}

			{enrollment ? (
				<EnrollmentFeedback />
			) : (
				<Form
					externalErrors={externalErrors}
					onSubmit={onSubmit}
					rules={rules({ st, priceType })}
					defaultValues={{
						has_accepted_terms: false,
						is_school_student: 'no',
						is_teacher: 'no',
						has_studied_before: 'no',
					}}
				>
					<Flex direction='column' gap='24'>
						<GridRow>
							<GridCol
								className={cx(gridCol({ colSpanM: '4', colSpanL: '4' }))}
							>
								<Form.InputText
									label={st('forms.enrollment.name.label')}
									name='name'
									withAsterisk={true}
								/>
							</GridCol>

							<GridCol
								className={cx(gridCol({ colSpanM: '8', colSpanL: '8' }))}
							>
								<Form.InputText
									label={st('forms.enrollment.surname.label')}
									name='surname'
									withAsterisk={true}
								/>
							</GridCol>

							<GridCol
								className={cx(gridCol({ colSpanM: '6', colSpanL: '6' }))}
							>
								<Form.Select
									name='gender'
									label={st('forms.enrollment.gender.label')}
									placeholder={st('forms.enrollment.gender.placeholder')}
									options={Object.values(GenderEnum).map((gender) => ({
										id: gender,
										value: st(`forms.enrollment.gender.${gender}.label`),
									}))}
									endSlot={
										<Icon name='unfold_more' classNames={icon({ size: 'm' })} />
									}
									withAsterisk={true}
								/>
							</GridCol>

							<GridCol
								className={cx(gridCol({ colSpanM: '6', colSpanL: '6' }))}
							>
								<Form.Select
									name='occupation'
									label={st('forms.enrollment.occupation.label')}
									placeholder={st('forms.enrollment.occupation.placeholder')}
									options={Object.values(OccupationEnum).map((occupation) => ({
										id: occupation,
										value: st(
											`forms.enrollment.occupation.${occupation}.label`,
										),
									}))}
									endSlot={
										<Icon name='unfold_more' classNames={icon({ size: 'm' })} />
									}
									withAsterisk={true}
								/>
							</GridCol>

							<GridCol
								className={cx(gridCol({ colSpanM: '4', colSpanL: '8' }))}
							>
								<Form.InputText
									label={st('forms.enrollment.address.label')}
									name='address'
									withAsterisk={true}
								/>
							</GridCol>

							<GridCol
								className={cx(gridCol({ colSpanM: '4', colSpanL: '4' }))}
							>
								<Form.InputText
									label={st('forms.enrollment.zip_code.label')}
									name='zip_code'
									type='number'
									inputMode='numeric'
									pattern='[0-9]{5}'
									maxLength={5}
									withAsterisk={true}
								/>
							</GridCol>

							<GridCol
								className={cx(gridCol({ colSpanM: '4', colSpanL: '4' }))}
							>
								<Form.InputText
									label={st('forms.enrollment.city.label')}
									name='city'
									withAsterisk={true}
								/>
							</GridCol>

							<GridCol
								className={cx(gridCol({ colSpanM: '6', colSpanL: '4' }))}
							>
								<Form.InputText
									name='country'
									label={st('forms.enrollment.nationality.label')}
									withAsterisk={true}
								/>
							</GridCol>

							<GridCol
								className={cx(gridCol({ colSpanM: '6', colSpanL: '4' }))}
							>
								<Form.DatePicker
									name='birthday'
									description=''
									label={st('forms.enrollment.birthday.label')}
									locale={locale}
									maxDate={new Date()}
									placeholder={st('forms.enrollment.birthday.placeholder')}
									icon={
										<Icon
											name='calendar_month'
											classNames={icon({ size: 's' })}
										/>
									}
									withAsterisk={true}
								/>
							</GridCol>

							<GridCol
								className={cx(gridCol({ colSpanM: '3', colSpanL: '3' }))}
							>
								<Form.Select
									name='document_type'
									label={st('forms.enrollment.document_type.label')}
									placeholder={st('forms.enrollment.document_type.placeholder')}
									options={Object.values(DocumentTypeEnum).map(
										(documentType) => ({
											id: documentType,
											value: st(
												`forms.enrollment.document_type.${documentType}`,
											),
										}),
									)}
									endSlot={
										<Icon name='unfold_more' classNames={icon({ size: 'm' })} />
									}
									withAsterisk={true}
								/>
							</GridCol>

							<GridCol
								className={cx(gridCol({ colSpanM: '4', colSpanL: '3' }))}
							>
								<Form.InputText
									name='document_number'
									label={st('forms.enrollment.document_number.label')}
									withAsterisk={true}
								/>
							</GridCol>

							<GridCol
								className={cx(gridCol({ colSpanM: '5', colSpanL: '6' }))}
							>
								<Form.InputFile
									name='identification_file'
									label={st('forms.enrollment.identification_file.label')}
									placeholder={st('forms.enrollment.upload_file')}
									description={st('forms.enrollment.upload_type')}
									multiple={true}
									withAsterisk={true}
									classNames={{
										root: cx(inputRoot(), inputFileRoot()),
									}}
									badgeClassName={inputFileBadge()}
								/>
							</GridCol>

							<GridCol
								className={cx(gridCol({ colSpanM: '3', colSpanL: '4' }))}
							>
								<Form.InputText
									label={st('forms.enrollment.phone.label')}
									name='phone'
									withAsterisk={true}
								/>
							</GridCol>

							<GridCol
								className={cx(gridCol({ colSpanM: '4', colSpanL: '4' }))}
							>
								<Form.InputText
									label={st('forms.enrollment.email.label')}
									name='email'
									withAsterisk={true}
								/>
							</GridCol>

							<GridCol
								className={cx(gridCol({ colSpanM: '5', colSpanL: '4' }))}
							>
								<Form.InputText
									label={st('forms.enrollment.confirm_email.label')}
									name='email_confirmation'
									withAsterisk={true}
								/>
							</GridCol>
						</GridRow>

						<Form.IfField field='birthday' condition={isUnder18}>
							<GridRow>
								<GridCol className={cx(gridCol({ colSpanL: '6' }))}>
									<Form.InputText
										label={st('forms.enrollment.tutor_phone.label')}
										name='tutor_phone'
										withAsterisk={true}
									/>
								</GridCol>

								<GridCol className={cx(gridCol({ colSpanL: '6' }))}>
									<Form.InputText
										label={st('forms.enrollment.tutor_email.label')}
										name='tutor_email'
										withAsterisk={true}
									/>
								</GridCol>
							</GridRow>
						</Form.IfField>

						{priceType === PriceTypeEnum.subsidized && (
							<GridRow className={bordersWrapper({ color: 'neutral' })}>
								<GridCol
									className={cx(gridCol({ colSpanM: '6', colSpanL: '6' }))}
								>
									<Form.Select
										name='subsidy_id'
										label={st('forms.enrollment.subsidy.label')}
										placeholder={st('forms.enrollment.subsidy.placeholder')}
										options={subsidies.map(({ id, ...subsidy }) => ({
											...subsidy,
											id: `${id}`,
											value: subsidy[`name_${locale}`],
										}))}
										withAsterisk={true}
										endSlot={
											<Icon
												name='unfold_more'
												classNames={icon({ size: 'm' })}
											/>
										}
									/>
								</GridCol>

								<GridCol className={gridCol({ colSpanM: '6', colSpanL: '6' })}>
									<Form.InputFile
										name='discount_accreditation_file'
										label={st(
											'forms.enrollment.discount_accreditation_file.label',
										)}
										placeholder={st('forms.enrollment.upload_file')}
										description={st('forms.enrollment.upload_type')}
										multiple={true}
										classNames={{
											root: cx(inputRoot(), inputFileRoot()),
										}}
										badgeClassName={inputFileBadge()}
									/>
								</GridCol>
							</GridRow>
						)}

						<GridRow>
							<GridCol
								className={cx(gridCol({ colSpanM: '4', colSpanL: '4' }))}
							>
								<Form.RadioGroup
									name='is_teacher'
									label={st('forms.enrollment.is_teacher.group_label')}
									value={isTeacher}
								>
									<Form.Radio
										key='is_teacher_yes'
										value='yes'
										label={st('forms.radio_button.yes.label')}
										onChange={() => setIsTeacher('yes')}
									/>
									<Form.Radio
										key='is_teacher_no'
										value='no'
										label={st('forms.radio_button.no.label')}
										onChange={() => setIsTeacher('no')}
									/>
								</Form.RadioGroup>
							</GridCol>

							<GridCol
								className={cx(gridCol({ colSpanM: '4', colSpanL: '4' }))}
							>
								<Form.Select
									name='teacher_type'
									label={st('forms.enrollment.teacher_type.label')}
									placeholder={st('forms.enrollment.teacher_type.placeholder')}
									disabled={isTeacher !== 'yes'}
									options={Object.values(TeacherTypeEnum).map(
										(teacherType) => ({
											id: teacherType,
											value: st(
												`forms.enrollment.teacher_type.${teacherType}.label`,
											),
										}),
									)}
									required={isTeacher !== 'no'}
									endSlot={
										<Icon name='unfold_more' classNames={icon({ size: 'm' })} />
									}
								/>
							</GridCol>
							<GridCol
								className={cx(
									gridCol({
										colSpanM: '3',
										colSpanL: '3',
										colStartM: '9',
										colStartL: '9',
									}),
								)}
							>
								<Form.RadioGroup
									name='is_school_student'
									label={st('forms.enrollment.is_school_student.group_label')}
								>
									<Form.Radio
										key='is_school_student_yes'
										value='yes'
										label={st('forms.radio_button.yes.label')}
									/>
									<Form.Radio
										key='is_school_student_no'
										value='no'
										label={st('forms.radio_button.no.label')}
									/>
								</Form.RadioGroup>
							</GridCol>
						</GridRow>

						<GridRow>
							<GridCol
								className={cx(gridCol({ colSpanM: '4', colSpanL: '4' }))}
							>
								<Form.RadioGroup
									name='has_studied_before'
									label={st('forms.enrollment.has_studied_before.group_label')}
									value={hasStudiedBefore}
								>
									<Form.Radio
										key='has_studied_before_yes'
										value='yes'
										label={st('forms.radio_button.yes.label')}
										onChange={() => setHasStudiedBefore('yes')}
									/>
									<Form.Radio
										key='has_studied_before_no'
										value='no'
										label={st('forms.radio_button.no.label')}
										onChange={() => setHasStudiedBefore('no')}
									/>
								</Form.RadioGroup>
							</GridCol>

							<GridCol
								className={cx(gridCol({ colSpanM: '8', colSpanL: '4' }))}
							>
								<GridCol className={cx(gridCol({ colSpanL: '6' }))}>
									<Form.InputText
										label={st('forms.enrollment.studied_duration.label')}
										name='studied_duration'
										required={hasStudiedBefore !== 'no'}
									/>
								</GridCol>
							</GridCol>

							<GridCol
								className={cx(gridCol({ colSpanM: '6', colSpanL: '6' }))}
							>
								<GridCol className={cx(gridCol({ colSpanL: '6' }))}>
									<Form.InputText
										label={st('forms.enrollment.self_assessed_level.label')}
										name='self_assessed_level'
										withAsterisk={true}
									/>
								</GridCol>
							</GridCol>

							<GridCol className={gridCol({ colSpanM: '6', colSpanL: '6' })}>
								<Form.InputFile
									name='level_accreditation_file'
									label={st('forms.enrollment.level_accreditation_file.label')}
									placeholder={st('forms.enrollment.upload_file')}
									description={st('forms.enrollment.upload_type')}
									classNames={{
										root: cx(inputRoot(), inputFileRoot()),
									}}
									badgeClassName={inputFileBadge()}
									multiple={true}
								/>
							</GridCol>
						</GridRow>

						<GridRow>
							<GridCol
								className={cx(gridCol({ colSpanM: '9', colSpanL: '9' }))}
							>
								<Form.Checkbox
									name='has_accepted_terms'
									label={
										<span
											dangerouslySetInnerHTML={{
												__html: st('forms.enrollment.has_accepted_terms.label'),
											}}
										/>
									}
								/>
							</GridCol>
							<GridCol
								className={cx(gridCol({ colSpanM: '3', colSpanL: '3' }))}
							>
								<div className={flexWrapper({ justifyM: 'end' })}>
									<Button type='submit' loading={isSubmitting}>
										{st('forms.enrollment.submit.label')}
									</Button>
								</div>
							</GridCol>
						</GridRow>
					</Flex>
				</Form>
			)}
		</Section>
	);
};
